import React, { useRef } from 'react'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion'

interface UnifiedSelection {
  id: string
  name: string
  power: number | null
  health: number | null
  maxHealth?: number | null
  cost: number
  imageUrl?: string
  equippedItems?: { name: string; description: string }[]
  keywords: string[]
  faction?: string
  type: string
  description: string
}

interface CardInspectorOverlayProps {
  selectedCard: UnifiedSelection | null
  selectedTheme?: 'FANTASY' | 'NOIR' | 'SUNSET'
  onClose: () => void
}

export function CardInspectorOverlay({ selectedCard, selectedTheme, onClose }: CardInspectorOverlayProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Parallax Values
  const x = useMotionValue(0)
  const y = useMotionValue(0)

  const mouseXSpring = useSpring(x)
  const mouseYSpring = useSpring(y)

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], [10, -10])
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], [-10, 10])

  if (!selectedCard) return null

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return
    const rect = containerRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    const xPct = mouseX / rect.width - 0.5
    const yPct = mouseY / rect.height - 0.5
    x.set(xPct)
    y.set(yPct)
  }

  const handleMouseLeave = () => {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-8 overflow-hidden"
      onClick={onClose}
    >
      {/* Cinematic Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-[40px]"
      />

      {/* Close Button */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={onClose}
        className="absolute top-12 right-12 w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 text-white/40 hover:text-white hover:bg-white/10 transition-colors z-50"
      >
        <X size={24} />
      </motion.button>

      <div
        className="relative w-full max-w-6xl flex flex-col lg:flex-row gap-16 items-center lg:items-center px-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left Side: The "Floating" Card */}
        <motion.div
          animate={{
            y: [0, -15, 0],
            rotateZ: [0, -1, 1, 0],
            rotateX: [0, 2, -2, 0],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="w-[380px] aspect-[5/7] shrink-0 pro-card-bg border border-white/20 shadow-[0_0_100px_rgba(0,0,0,0.8)] relative"
        >
          <div className="absolute inset-2 border border-white/5 pointer-events-none z-10" />

          <div className="absolute inset-0 overflow-hidden">
            {selectedCard.imageUrl ? (
              <img
                src={selectedCard.imageUrl}
                className="w-full h-full object-cover brightness-110 saturate-[1.1]"
                alt={selectedCard.name}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-8xl font-theme text-white/5">
                {selectedCard.name[0]}
              </div>
            )}
          </div>

          {/* Floating Stats */}
          <div className="absolute inset-0 p-8 flex flex-col justify-between pointer-events-none z-20">
            <div className="w-16 h-16 rounded-full border-2 border-white/20 bg-white/10 backdrop-blur-2xl flex items-center justify-center text-2xl font-black text-white shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
              {selectedCard.cost}
            </div>
            <div className="flex justify-between items-end">
              {selectedCard.power !== null && (
                <div className="w-20 h-20 border-2 border-white/10 bg-black/60 backdrop-blur-2xl flex items-center justify-center text-5xl font-black text-white rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.6)]">
                  {selectedCard.power}
                </div>
              )}
              {selectedCard.health !== null && (
                <div className="w-20 h-20 border-2 border-red-500/30 bg-red-950/60 backdrop-blur-2xl flex items-center justify-center text-5xl font-black text-red-500 rounded-xl shadow-[0_10px_40px_rgba(239,68,68,0.2)]">
                  {selectedCard.health}
                </div>
              )}
            </div>
          </div>

          <div className="absolute inset-0 pointer-events-none z-30">
            {selectedCard.keywords.includes('ward') && <div className="absolute inset-0 border-4 border-cyan-400/20 shadow-[inset_0_0_60px_rgba(34,211,238,0.2)]" />}
            {selectedCard.keywords.includes('lethal') && <div className="absolute inset-0 border-4 border-lime-400/20 shadow-[inset_0_0_60px_rgba(163,230,53,0.2)]" />}
          </div>
        </motion.div>

        {/* Right Side: Cinematic Content */}
        <motion.div
          initial={{ x: 40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex-1 flex flex-col justify-center gap-10 max-w-2xl"
        >
          <div className="space-y-4">
            <div className="flex items-center gap-6">
              <span className="text-xs font-black text-theme-primary uppercase tracking-[0.6em] px-4 py-1.5 bg-theme-primary/10 border border-theme-primary/20 rounded-sm">
                {selectedCard.faction || 'Neutral'}
              </span>
              <span className="text-xs font-black text-white/30 uppercase tracking-[0.4em]">
                {selectedCard.type}
              </span>
            </div>

            <h2 className="text-8xl font-theme text-white uppercase tracking-tighter leading-[0.85] font-black">
              {selectedCard.name}
            </h2>
          </div>

          <div className="relative">
            <p className="text-xl font-theme-body text-white/80 leading-relaxed font-light">
              {selectedCard.description}
            </p>
          </div>

          <div className="space-y-8">
            {selectedCard.keywords.length > 0 && (
              <div className="flex flex-wrap gap-4">
                {selectedCard.keywords.map((k) => (
                  <motion.div
                    key={k}
                    whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.1)' }}
                    className="text-xs px-6 py-3 bg-white/5 border border-white/10 text-white uppercase font-black tracking-[0.3em] cursor-help rounded-sm"
                  >
                    {k}
                  </motion.div>
                ))}
              </div>
            )}

            {selectedCard.equippedItems && selectedCard.equippedItems.length > 0 && (
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="h-[1px] flex-1 bg-white/10" />
                  <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.5em] whitespace-nowrap">Integrated Modifications</span>
                  <div className="h-[1px] flex-1 bg-white/10" />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  {selectedCard.equippedItems.map((item, i) => (
                    <div key={i} className="group/item bg-white/[0.02] border border-white/5 p-6 rounded-sm transition-all hover:bg-white/[0.05] hover:border-white/10">
                      <div className="text-lg font-black text-white uppercase tracking-widest mb-2 group-hover/item:text-theme-primary transition-colors">{item.name}</div>
                      <div className="text-sm text-white/40 font-theme-body leading-relaxed">{item.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}
